/**
 * 제휴 혜택 탭 컨텐츠 컴포넌트
 */
import { useLanguage } from '@/contexts/AppContext';

export function PartnershipsTabContent() {
  return (
    <>
      {/* Card Partnership Discounts */}
      <CardPartnershipSection />

      {/* Telecom Partnership Discounts */}
      <TelecomPartnershipSection />

      {/* Special Promotions */}
      <SpecialPromotionsSection />

      {/* Partnership Guide */}
      <PartnershipGuideSection />
    </>
  );
}

// 카드사 제휴 할인 섹션
function CardPartnershipSection() {
  const { language } = useLanguage();
  return (
    <div className="bg-background rounded-[10px] p-4 mb-3 shadow-md">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-[15px] font-bold text-foreground">
          {language === 'ko' ? '카드사 제휴 할인' : language === 'es' ? 'Descuentos de tarjetas' : 'Card Partnership Discounts'}
        </h3>
        <span className="text-[12px] text-muted-foreground font-bold">
          4{language === 'ko' ? '개' : ''}
        </span>
      </div>

      {/* Card Discount List */}
      <div className="space-y-3">
        {/* Samsung Card */}
        <CardDiscountItem
          cardName="SAMSUNG"
          cardColor="#1428A0"
          brandName={language === 'ko' ? '삼성카드' : 'Samsung Card'}
          badgeType="instant"
          badgeText={language === 'ko' ? '즉시할인' : language === 'es' ? 'Inmediato' : 'Instant'}
          description={language === 'ko' ? '전 메뉴 5% 즉시할인' : language === 'es' ? '5% de descuento en todos los menús' : '5% OFF all menus'}
          condition={language === 'ko' ? '월 최대 ₩10,000 할인' : language === 'es' ? 'Máx. ₩10,000/mes' : 'Max ₩10,000/month'}
          validity={language === 'ko' ? '~ 2025.12.31' : language === 'es' ? 'Hasta 31/12/2025' : 'Until Dec 31, 2025'}
        />

        {/* Hyundai Card */}
        <CardDiscountItem
          cardName="HYUNDAI"
          cardColor="#000000"
          brandName={language === 'ko' ? '현대카드' : 'Hyundai Card'}
          badgeType="points"
          badgeText={language === 'ko' ? '포인트' : language === 'es' ? 'Puntos' : 'Points'}
          description={language === 'ko' ? '10% M포인트 적립' : language === 'es' ? '10% de M Points' : '10% M Points'}
          condition={language === 'ko' ? '₩30,000 이상 결제 시' : language === 'es' ? 'En compras sobre ₩30,000' : 'On purchases over ₩30,000'}
          validity={language === 'ko' ? '~ 2025.12.31' : language === 'es' ? 'Hasta 31/12/2025' : 'Until Dec 31, 2025'}
        />

        {/* Shinhan Card */}
        <CardDiscountItem
          cardName="신한"
          cardColor="#0046FF"
          brandName={language === 'ko' ? '신한카드' : 'Shinhan Card'}
          badgeType="firstPay"
          badgeText={language === 'ko' ? '첫 결제' : language === 'es' ? '1er pago' : '1st Pay'}
          description={language === 'ko' ? '₩3,000 즉시할인' : language === 'es' ? '₩3,000 de descuento' : '₩3,000 OFF'}
          condition={language === 'ko' ? '신규 가입 후 첫 결제 시' : language === 'es' ? 'Primer pago tras registro' : 'First payment after signup'}
          validity={language === 'ko' ? '가입 후 30일간' : language === 'es' ? '30 días tras registro' : '30 days after signup'}
        />

        {/* KB Card */}
        <CardDiscountItem
          cardName="KB"
          cardColor="#FFBF00"
          brandName={language === 'ko' ? 'KB국민카드' : 'KB Card'}
          badgeType="instant"
          badgeText={language === 'ko' ? '즉시할인' : language === 'es' ? 'Inmediato' : 'Instant'}
          description={language === 'ko' ? '주말 7% 즉시할인' : language === 'es' ? '7% de descuento fin de semana' : '7% OFF on weekends'}
          condition={language === 'ko' ? '금·토·일 결제 시' : language === 'es' ? 'Vie-Dom' : 'Fri-Sun'}
          validity={language === 'ko' ? '~ 2025.12.31' : language === 'es' ? 'Hasta 31/12/2025' : 'Until Dec 31, 2025'}
        />
      </div>
    </div>
  );
}

// 카드 할인 아이템
interface CardDiscountItemProps {
  cardName: string;
  cardColor: string;
  brandName: string;
  badgeType: 'instant' | 'points' | 'firstPay';
  badgeText: string;
  description: string;
  condition: string;
  validity: string;
}

function CardDiscountItem({
  cardName,
  cardColor,
  brandName,
  badgeType,
  badgeText,
  description,
  condition,
  validity
}: CardDiscountItemProps) {
  const badgeColors = {
    instant: 'bg-primary/15 text-primary',
    points: 'bg-secondary/15 text-secondary',
    firstPay: 'bg-warning/15 text-warning'
  };

  return (
    <div className="bg-muted rounded-[10px] p-4 hover:bg-border transition-all active:scale-[0.98]">
      <div className="flex items-start gap-4">
        <div className="bg-background rounded-[8px] w-16 h-11 flex items-center justify-center shrink-0 shadow-sm">
          <span className={`text-[11px] font-bold`} style={{ color: cardColor }}>{cardName}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <h4 className="text-[14px] font-bold text-foreground">{brandName}</h4>
            <span className={`text-[10px] px-2 py-1 rounded-full font-bold ${badgeColors[badgeType]}`}>
              {badgeText}
            </span>
          </div>
          <p className="text-[13px] text-foreground mb-2 font-bold">{description}</p>
          <p className="text-[11px] text-muted-foreground mb-1 font-medium">{condition}</p>
          <p className="text-[10px] text-muted-foreground font-medium">{validity}</p>
        </div>
      </div>
    </div>
  );
}

// 통신사 제휴 할인 섹션
function TelecomPartnershipSection() {
  const { language } = useLanguage();
  return (
    <div className="bg-background rounded-[10px] p-4 mb-3 shadow-md">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-[15px] font-bold text-foreground">
          {language === 'ko' ? '통신사 제휴 할인' : language === 'es' ? 'Descuentos de telecomunicaciones' : 'Telecom Discounts'}
        </h3>
        <span className="text-[12px] text-muted-foreground font-bold">
          3{language === 'ko' ? '개' : ''}
        </span>
      </div>

      {/* Telecom Discount List */}
      <div className="space-y-3">
        {/* SK Telecom */}
        <TelecomDiscountItem
          logoText="SK"
          logoColor="#FF0055"
          bgColor="rgba(255,0,85,0.05)"
          borderColor="rgba(255,0,85,0.2)"
          title={language === 'ko' ? 'SK텔레콤 T멤버십 할인' : language === 'es' ? 'SK Telecom T Membership' : 'SK Telecom T Membership'}
          description={language === 'ko' ? 'T멤버십 회원 5% 할인 + 포인트 3% 적립' : language === 'es' ? '5% OFF + 3% de puntos para miembros T' : '5% OFF + 3% points for T members'}
          badgeText={language === 'ko' ? '멤버십' : language === 'es' ? 'Membresía' : 'Membership'}
        />

        {/* KT */}
        <TelecomDiscountItem
          logoText="KT"
          logoColor="#FF0000"
          bgColor="rgba(255,0,0,0.05)"
          borderColor="rgba(255,0,0,0.2)"
          title={language === 'ko' ? 'KT 멤버십 특별 할인' : language === 'es' ? 'KT Membership Especial' : 'KT Membership Special'}
          description={language === 'ko' ? '월 1회 ₩5,000 할인쿠폰 + 결제금액 2% 적립' : language === 'es' ? 'Cupón de ₩5,000 mensual + 2% de acumulación' : '₩5,000 monthly coupon + 2% cashback'}
          badgeText={language === 'ko' ? '멤버십' : language === 'es' ? 'Membresía' : 'Membership'}
        />

        {/* LG U+ */}
        <TelecomDiscountItem
          logoText="LG U+"
          logoColor="#A6007F"
          bgColor="rgba(166,0,127,0.05)"
          borderColor="rgba(166,0,127,0.2)"
          title={language === 'ko' ? 'LG U+ 멤버십 혜택' : language === 'es' ? 'LG U+ Membership' : 'LG U+ Membership'}
          description={language === 'ko' ? '전 메뉴 3% 할인 + U+포인트 5% 적립' : language === 'es' ? '3% OFF + 5% de U+Points' : '3% OFF + 5% U+Points'}
          badgeText={language === 'ko' ? '멤버십' : language === 'es' ? 'Membresía' : 'Membership'}
        />
      </div>
    </div>
  );
}

// 통신사 할인 아이템
interface TelecomDiscountItemProps {
  logoText: string;
  logoColor: string;
  bgColor: string;
  borderColor: string;
  title: string;
  description: string;
  badgeText: string;
}

function TelecomDiscountItem({
  logoText,
  logoColor,
  bgColor,
  borderColor,
  title,
  description,
  badgeText
}: TelecomDiscountItemProps) {
  return (
    <div
      className="rounded-[5px] p-[14px] border"
      style={{ backgroundColor: bgColor, borderColor }}
    >
      <div className="flex items-start gap-[14px]">
        <div className="bg-background rounded-[5px] w-[60px] h-[40px] flex items-center justify-center shrink-0 shadow-sm border border-border">
          <span className="text-[14px] font-bold" style={{ color: logoColor }}>{logoText}</span>
        </div>
        <div className="flex-1">
          <h4 className="text-[14px] font-medium text-foreground mb-[3.5px]">{title}</h4>
          <p className="text-[12px] text-muted-foreground mb-[7px]">{description}</p>
          <div className="flex items-center gap-[7px]">
            <span
              className="text-[10.5px] px-[7px] py-[3.5px] rounded-full"
              style={{ backgroundColor: `${logoColor}33`, color: logoColor }}
            >
              {badgeText}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// 특별 프로모션 섹션
function SpecialPromotionsSection() {
  const { language } = useLanguage();
  return (
    <div className="bg-background rounded-[10px] p-4 mb-3 shadow-md">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-[15px] font-bold text-foreground">
          {language === 'ko' ? '특별 프로모션' : language === 'es' ? 'Promociones especiales' : 'Special Promotions'}
        </h3>
        <span className="text-[12px] text-muted-foreground font-bold">
          2{language === 'ko' ? '개' : ''}
        </span>
      </div>

      {/* Promotion List */}
      <div className="space-y-3">
        {/* Happy Hour */}
        <PromotionCard
          percentage="20%"
          icon="⏰"
          title={language === 'ko' ? '해피아워 타임 할인' : language === 'es' ? 'Descuento Happy Hour' : 'Happy Hour Discount'}
          description={language === 'ko' ? '평일 오후 2시~4시 전 메뉴 20% 할인' : language === 'es' ? 'Lun-Vie 14:00-16:00, 20% OFF en todos los menús' : 'Mon-Fri 2PM-4PM, 20% OFF all menus'}
          timing={language === 'ko' ? '매주 반복' : language === 'es' ? 'Cada semana' : 'Weekly'}
          buttonText={language === 'ko' ? '사용' : language === 'es' ? 'Usar' : 'Use'}
          accentColor="#03d67b"
        />

        {/* Student Discount */}
        <PromotionCard
          percentage="15%"
          icon="🎓"
          title={language === 'ko' ? '학생 할인' : language === 'es' ? 'Descuento estudiantil' : 'Student Discount'}
          description={language === 'ko' ? '학생증 제시 시 15% 할인 (대학생 이상)' : language === 'es' ? 'Descuento del 15% al presentar tarjeta de estudiante' : '15% OFF with valid student ID'}
          timing={language === 'ko' ? '연중무휴' : language === 'es' ? 'Todo el año' : 'Year-round'}
          buttonText={language === 'ko' ? '사용' : language === 'es' ? 'Usar' : 'Use'}
          accentColor="#7a4ffc"
        />
      </div>
    </div>
  );
}

// 프로모션 카드
interface PromotionCardProps {
  percentage: string;
  icon: string;
  title: string;
  description: string;
  timing: string;
  buttonText: string;
  accentColor: string;
}

function PromotionCard({
  percentage,
  icon,
  title,
  description,
  timing,
  buttonText,
  accentColor
}: PromotionCardProps) {
  const isGreen = accentColor === '#03d67b';

  return (
    <div
      className={`rounded-[10px] p-4 border-2 shadow-sm ${
        isGreen ? 'bg-primary/10 border-primary/30' : 'bg-secondary/10 border-secondary/30'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[28px] font-bold leading-none" style={{ color: accentColor }}>{percentage}</span>
            <span className="text-[14px] font-bold" style={{ color: accentColor }}>
              할인
            </span>
            <span className="text-[20px]">{icon}</span>
          </div>
          <h4 className="text-[14px] font-bold text-foreground mb-2">{title}</h4>
          <p className="text-[12px] text-foreground mb-2 font-medium">{description}</p>
          <div className="flex items-center gap-1.5">
            <svg className="w-3 h-3" fill="none" viewBox="0 0 11 11">
              <path d="M5.25 2.625V5.25L7 6.125" stroke="currentColor" className="text-muted-foreground" strokeLinecap="round" strokeLinejoin="round" strokeWidth="0.875" />
              <path d="M5.25 9.625C7.66625 9.625 9.625 7.66625 9.625 5.25C9.625 2.83375 7.66625 0.875 5.25 0.875C2.83375 0.875 0.875 2.83375 0.875 5.25C0.875 7.66625 2.83375 9.625 5.25 9.625Z" stroke="currentColor" className="text-muted-foreground" strokeLinecap="round" strokeLinejoin="round" strokeWidth="0.875" />
            </svg>
            <span className="text-[11px] text-muted-foreground font-bold">{timing}</span>
          </div>
        </div>
        <button
          className={`text-white rounded-[8px] px-4 py-2 text-[12px] font-bold hover:opacity-90 active:scale-95 transition-all shrink-0 shadow-md`}
          style={{ backgroundColor: accentColor }}
        >
          {buttonText}
        </button>
      </div>
    </div>
  );
}

// 제휴 혜택 안내 섹션
function PartnershipGuideSection() {
  const { language } = useLanguage();
  const guideItems = [
    language === 'ko' ? '제휴 카드는 결제 시 자동으로 할인이 적용됩니다' : language === 'es' ? 'Los descuentos de tarjetas se aplican automáticamente al pagar' : 'Card discounts are applied automatically at payment',
    language === 'ko' ? '멤버십 할인은 앱 로그인 후 이용 가능합니다' : language === 'es' ? 'Los descuentos de membresía están disponibles después de iniciar sesión' : 'Membership discounts available after login',
    language === 'ko' ? '할인 혜택은 중복 적용이 불가능할 수 있습니다' : language === 'es' ? 'Los descuentos pueden no ser acumulables' : 'Discounts may not be combinable',
    language === 'ko' ? '프로모션 기간과 조건은 변경될 수 있습니다' : language === 'es' ? 'Los períodos y condiciones promocionales pueden cambiar' : 'Promotional periods and terms are subject to change'
  ];

  return (
    <div className="bg-background rounded-[10px] p-4 shadow-md">
      <div className="flex items-center gap-3 mb-4">
        <div className="flex items-center justify-center w-8 h-8 bg-secondary/10 rounded-[8px] shrink-0">
          <span className="text-[18px]">🤝</span>
        </div>
        <h3 className="text-[15px] font-bold text-foreground">
          {language === 'ko' ? '제휴 혜택 안내' : language === 'es' ? 'Guía de beneficios' : 'Partnership Guide'}
        </h3>
      </div>

      <div className="space-y-3">
        {guideItems.map((item, index) => (
          <div key={index} className="flex items-start gap-3 p-3 rounded-[8px] bg-muted">
            <div className="w-1.5 h-1.5 bg-secondary rounded-full shrink-0 mt-1.5" />
            <p className="text-[13px] text-foreground flex-1">{item}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
